import { datadogRum } from '@datadog/browser-rum'
import { Admin } from '@models/Admin'
import { useAuth } from '@modules/auth/contexts/Auth'
import { useTenant } from '@modules/tenant/contexts/tenant/TenantContext'
import { GleapIdentify } from '@services/GleapService'
import { useFeatureFlagsContext } from '@utils/featureFlags'
import { useEffect, useRef } from 'react'

/**
 * Identifies the logged-in admin to Gleap, Datadog RUM and the Datadog feature
 * flags as soon as currentUser is known, including on page reload where the
 * session is restored via getLoggedUserInfos() without replaying the login
 * callback. Without this an already-logged-in admin keeps whatever the SDKs
 * cached at their last login: Gleap only picks up the tenant `company` after a
 * logout/login, RUM sessions carry no user at all, and flags stay on their
 * anonymous values.
 */
const SessionIdentity = () => {
  const { currentUser } = useAuth<Admin, {}>()
  const { tenant } = useTenant()
  const gleapIdentifiedUserId = useRef<number | null>(null)

  // Back to anonymous on logout, when currentUser goes undefined
  useFeatureFlagsContext(currentUser, tenant)

  // Purely local, so no guard needed. The matching clearUser() stays in the
  // loggedOut callback.
  useEffect(() => {
    if (!currentUser) return
    datadogRum.setUser({
      id: currentUser.id.toString(),
      name: `${currentUser.firstName} ${currentUser.lastName}`,
      email: currentUser.email,
    })
  }, [currentUser])

  // GleapIdentify starts with an HTTP call for the verification hash, so we
  // guard on the user id via a ref to fire exactly once per session, and reset
  // it on logout (currentUser back to undefined) so a re-login re-identifies.
  useEffect(() => {
    if (!currentUser) {
      gleapIdentifiedUserId.current = null
      return
    }
    if (gleapIdentifiedUserId.current === currentUser.id) return
    gleapIdentifiedUserId.current = currentUser.id
    // Never let this reject: an unhandled rejection pollutes RUM, and the hash
    // request goes through the global 401 interceptor that logs the admin out.
    GleapIdentify(currentUser, tenant).catch((error) => {
      console.error('Gleap identification failed', error)
    })
  }, [currentUser, tenant])

  return null
}

export default SessionIdentity
